import { useMemo } from 'react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, Tooltip as RechartsTooltip,
} from 'recharts';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';

interface CategoryData {
  category: string;
  multiplier: number;
  procedureCount: number;
}

interface CountryRadarChartProps {
  data: CategoryData[];
  countryName: string;
  maxSpokes?: number;
}

export function CountryRadarChart({
  data,
  countryName,
  maxSpokes = 10,
}: CountryRadarChartProps) {
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';

  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.multiplier - a.multiplier)
      .slice(0, maxSpokes)
      .map(d => ({
        ...d,
        // Shorten for radar labels
        label: d.category.length > 16 ? d.category.slice(0, 14) + '...' : d.category,
        fullDomain: Math.ceil(d.multiplier),
      }));
  }, [data, maxSpokes]);

  const accentColor = isAhp ? '#C5A55A' : '#00ff41';
  const fillColor = isAhp ? 'rgba(197,165,90,0.25)' : 'rgba(0,255,65,0.2)';
  const gridColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const textColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';
  const tooltipBg = isAhp ? (isDark ? '#0f1d2f' : '#fff') : (isDark ? '#111' : '#1a1a2e');
  const tooltipBorder = isAhp ? (isDark ? 'rgba(197,165,90,0.3)' : '#e5e7eb') : 'rgba(255,255,255,0.15)';

  if (!chartData.length) return null;

  const maxMultiplier = Math.ceil(Math.max(...chartData.map(d => d.multiplier)) * 1.1);

  return (
    <ResponsiveContainer width="100%" height={360}>
      <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="75%">
        <PolarGrid stroke={gridColor} />
        <PolarAngleAxis
          dataKey="label"
          tick={{ fill: textColor, fontSize: 10 }}
        />
        <PolarRadiusAxis
          angle={90}
          domain={[0, maxMultiplier]}
          tick={{ fill: textColor, fontSize: 9 }}
          tickFormatter={(v: number) => `${v}x`}
        />
        <RechartsTooltip
          contentStyle={{
            backgroundColor: tooltipBg,
            border: `1px solid ${tooltipBorder}`,
            borderRadius: '8px',
            color: isDark ? '#fff' : '#1B1B1B',
            fontSize: '12px',
          }}
          formatter={(value: number) => [`${value.toFixed(1)}x`, 'US Markup']}
          labelFormatter={(label: string) => {
            const item = chartData.find(d => d.label === label);
            return item ? `${item.category} (${item.procedureCount} procedures)` : label;
          }}
        />
        <Radar
          name={countryName}
          dataKey="multiplier"
          stroke={accentColor}
          fill={fillColor}
          strokeWidth={2}
          dot={{ fill: accentColor, r: 3 }}
        />
      </RadarChart>
    </ResponsiveContainer>
  );
}
